/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useApolloClient, useMutation } from '@apollo/client'
import { Close } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import {
  SABRE_GET_PRODUCTS_SEARCH,
  SUBMIT_CLASSIFICATION_BATCH,
  UltraCompanyChoices,
  type SabreGetProductsSearchQuery,
  type SabreGetProductsSearchQueryVariables,
} from '@dai/graphql'

import useT from '@/i18n/useT'

import { CropSummaryCard, VisualSearchCropCard } from './explorerCards'
import {
  formatUltraCompanyChoices,
  normalizeUpcKey,
  type ExplorerProduct,
  type LabelingExplorerBox,
  type LabelingExplorerImage,
} from './explorerUtils'
import type { SearchProduct } from './labelingHelpers'
import ProductSearchAutocomplete from './ProductSearchAutocomplete'
import { SOURCE_CUSTOMERS, type VisualSearchState } from './explorerUrlParams'

const VisualSearchPanel = ({
  visualSearch,
  sourceCustomers,
  onSourceCustomersChange,
  productsByUpc,
  closeConfidenceThreshold,
  cropTileSize,
  onClearSearch,
  onOpenCrop,
}: {
  visualSearch: VisualSearchState
  sourceCustomers: UltraCompanyChoices[]
  onSourceCustomersChange: (sc: UltraCompanyChoices[]) => void
  productsByUpc: Record<string, ExplorerProduct>
  closeConfidenceThreshold: number | null
  cropTileSize: number
  onClearSearch: () => void
  onOpenCrop: (image: LabelingExplorerImage, box: LabelingExplorerBox) => void
}) => {
  const { t } = useT()
  const apolloClient = useApolloClient()
  const [selectedBoxIds, setSelectedBoxIds] = React.useState<Set<string>>(
    new Set(),
  )
  const [verifyProduct, setVerifyProduct] =
    React.useState<SearchProduct | null>(null)
  const [verifySuggestedProducts, setVerifySuggestedProducts] = React.useState<
    SearchProduct[]
  >([])
  const [isVerifying, setIsVerifying] = React.useState(false)
  const [displayLimit, setDisplayLimit] = React.useState(100)

  const [submitBatch] = useMutation(SUBMIT_CLASSIFICATION_BATCH, {
    context: { endPoint: 'mlUltra' },
  })

  const prefillVerifyFromBox = React.useCallback(
    async (box: LabelingExplorerBox) => {
      const topPredictions = [...box.predictedUpcs]
        .sort((a, b) => (a.classifierRank ?? 999) - (b.classifierRank ?? 999))
        .slice(0, 5)
      if (topPredictions.length === 0) return
      const results = await Promise.all(
        topPredictions.map(p =>
          apolloClient
            .query<
              SabreGetProductsSearchQuery,
              SabreGetProductsSearchQueryVariables
            >({
              query: SABRE_GET_PRODUCTS_SEARCH,
              context: { endPoint: 'sabre' },
              variables: { input: { searchTerm: p.upc, limit: 1 } },
              fetchPolicy: 'cache-first',
            })
            .then(r => r.data?.products?.results?.[0] ?? null)
            .catch(() => null),
        ),
      )
      const products = results.filter((p): p is SearchProduct => p !== null)
      if (products.length === 0) return
      setVerifySuggestedProducts(products)
      if (box.currentUpc) {
        const top =
          products.find(
            p => normalizeUpcKey(p.upcA) === normalizeUpcKey(box.currentUpc!),
          ) ?? products[0]
        setVerifyProduct(top)
      }
    },
    [apolloClient],
  )

  // Prefill verify product from anchor on mount (key prop handles reset between searches)
  React.useEffect(() => {
    const anchor = visualSearch.anchorItem?.box
    if (anchor?.predictedUpcs?.length) {
      void prefillVerifyFromBox(anchor)
    }
  }, [prefillVerifyFromBox, visualSearch.anchorItem?.box])

  const handleBulkVerify = async () => {
    const upc = verifyProduct?.upcA
    if (selectedBoxIds.size === 0 || !upc) return
    setIsVerifying(true)
    try {
      await submitBatch({
        variables: {
          input: {
            upc,
            acceptedBoxIds: Array.from(selectedBoxIds),
            rejectedBoxIds: [],
          },
        },
      })
      setSelectedBoxIds(new Set())
      setVerifyProduct(null)
      setVerifySuggestedProducts([])
    } finally {
      setIsVerifying(false)
    }
  }

  const boxItems = visualSearch.items.filter(i => i.box)

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="h6" sx={{ flex: 1 }}>
          {t('visualSearch.results.title', 'Visual Search Results')}
          {visualSearch.items.length > 0 && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              ({visualSearch.items.length})
            </Typography>
          )}
        </Typography>
        <TextField
          select
          size="small"
          label={t('visualSearch.resultsFrom.label', 'Results from')}
          InputLabelProps={{ shrink: true }}
          value={sourceCustomers}
          onChange={e => {
            const val = e.target.value
            onSourceCustomersChange(
              Array.isArray(val) ? (val as UltraCompanyChoices[]) : [],
            )
          }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: selected => {
              const arr = selected as UltraCompanyChoices[]
              return arr.length === 0
                ? t('visualSearch.resultsFrom.allCustomers', 'All customers')
                : arr.map(formatUltraCompanyChoices).join(', ')
            },
          }}
          sx={{ minWidth: 160 }}
        >
          {SOURCE_CUSTOMERS.map(customer => (
            <MenuItem key={customer} value={customer}>
              {formatUltraCompanyChoices(customer)}
            </MenuItem>
          ))}
        </TextField>
        <Button size="small" startIcon={<Close />} onClick={onClearSearch}>
          {t('visualSearch.actions.clearSearch', 'Clear search')}
        </Button>
      </Stack>

      <Paper sx={{ p: 1.5, borderRadius: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {t('visualSearch.anchor.label', 'Search anchor')}
        </Typography>
        {visualSearch.anchorItem ? (
          <Box sx={{ display: 'inline-block' }}>
            <CropSummaryCard
              item={visualSearch.anchorItem}
              productsByUpc={productsByUpc}
              closeConfidenceThreshold={closeConfidenceThreshold}
              tileSize={cropTileSize}
              onOpen={onOpenCrop}
            />
          </Box>
        ) : visualSearch.anchorImagePreview ? (
          <Box
            component="img"
            src={visualSearch.anchorImagePreview}
            alt={t('visualSearch.anchor.alt', 'Search anchor')}
            sx={{
              maxHeight: 160,
              maxWidth: '100%',
              borderRadius: 1,
              display: 'block',
            }}
          />
        ) : null}
      </Paper>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              indeterminate={
                selectedBoxIds.size > 0 && selectedBoxIds.size < boxItems.length
              }
              checked={
                boxItems.length > 0 && selectedBoxIds.size === boxItems.length
              }
              onChange={event => {
                if (event.target.checked) {
                  setSelectedBoxIds(
                    new Set(
                      visualSearch.items
                        .map(i => i.box?.id)
                        .filter((id): id is string => Boolean(id)),
                    ),
                  )
                } else {
                  setSelectedBoxIds(new Set())
                }
              }}
            />
          }
          label={t('visualSearch.selection.selectedCount', {
            count: selectedBoxIds.size,
            defaultValue: `${selectedBoxIds.size} selected`,
          })}
        />
        <Box sx={{ flex: 1 }} />
        <ProductSearchAutocomplete
          size="small"
          minWidth={280}
          value={verifyProduct}
          suggestedProducts={verifySuggestedProducts}
          label={t('visualSearch.verify.productLabel', 'Product to verify')}
          placeholder={t(
            'visualSearch.verify.productPlaceholder',
            'Name, UPC, or brand',
          )}
          onChange={setVerifyProduct}
        />
        <Button
          variant="contained"
          disabled={selectedBoxIds.size === 0 || !verifyProduct || isVerifying}
          onClick={() => void handleBulkVerify()}
        >
          {isVerifying
            ? t('visualSearch.verify.verifying', 'Verifying…')
            : t('visualSearch.verify.action', {
                count: selectedBoxIds.size,
                defaultValue: `Verify ${selectedBoxIds.size} crop${selectedBoxIds.size !== 1 ? 's' : ''}`,
              })}
        </Button>
      </Stack>

      {visualSearch.error && (
        <Paper sx={{ p: 2, borderRadius: 1 }}>
          <Typography color="error">{visualSearch.error}</Typography>
        </Paper>
      )}

      {visualSearch.loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('visualSearch.loading', 'Finding similar crops…')}
          </Typography>
        </Stack>
      ) : visualSearch.items.length === 0 ? (
        <>
          <Divider />
          <Typography align="center" color="text.secondary">
            {t('visualSearch.empty', 'No similar crops found.')}
          </Typography>
        </>
      ) : (
        <InfiniteScroll
          dataLength={Math.min(displayLimit, visualSearch.items.length)}
          next={() => setDisplayLimit(n => n + 100)}
          hasMore={displayLimit < visualSearch.items.length}
          loader={
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          }
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fill, minmax(min(${cropTileSize}px, 100%), ${cropTileSize}px))`,
              gap: 1,
            }}
          >
            {visualSearch.items.slice(0, displayLimit).map(item => (
              <VisualSearchCropCard
                key={item.cursor}
                item={item}
                selected={Boolean(item.box && selectedBoxIds.has(item.box.id))}
                setSelectedBoxIds={setSelectedBoxIds}
                onOpen={onOpenCrop}
                productsByUpc={productsByUpc}
                closeConfidenceThreshold={closeConfidenceThreshold}
                tileSize={cropTileSize}
              />
            ))}
          </Box>
        </InfiniteScroll>
      )}
    </Stack>
  )
}

export default VisualSearchPanel
